@file:Suppress("ktlint:standard:max-line-length")

package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import com.royceecoleman.bill.shared.billing.WordingCheck
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** How a check reads in a list, e.g. regarding → "re" · blocks approval. */
internal fun wordingCheckSummary(check: WordingCheck): String =
    buildString {
        append(check.pattern)
        when (val replacement = check.replacement) {
            null -> append(" · flag only")
            "" -> append(" → delete")
            else -> append(" → \"$replacement\"")
        }
        append(if (check.blocking) " · blocks approval" else " · warns")
    }

/** Moves [suggestion] into the list being edited, unless a check with the same pattern is already there. */
internal fun acceptSuggestion(
    checks: List<WordingCheck>,
    suggestion: WordingCheck,
): List<WordingCheck> =
    if (checks.any { it.pattern.trim().equals(suggestion.pattern.trim(), ignoreCase = true) }) checks else checks + suggestion

/**
 * The lawyer's wording checks: edited as one list and saved together. BILL can suggest checks from the standing rules;
 * suggestions are only added to the list being edited, so nothing is saved until the lawyer saves.
 */
@Composable
internal fun WordingChecksCard(
    busy: Boolean,
    checks: List<WordingCheck>,
    dirty: Boolean,
    suggestions: List<WordingCheck>,
    canSuggest: Boolean,
    chatsRemaining: Int,
    change: (List<WordingCheck>) -> Unit,
    changeSuggestions: (List<WordingCheck>) -> Unit,
    save: () -> Unit,
    discard: () -> Unit,
    suggest: () -> Unit,
) {
    Div({ classes("bill-card") }) {
        H2 { Text("Wording checks") }
        P({ classes("bill-muted") }) {
            Text(
                "BILL runs these checks in code on every entry, after drafting and after each of your edits, so they never miss. A check that blocks stops approval until the wording is fixed; others warn. When a check has a replacement, review offers a one-click fix. The rules check after drafting also sees what they find.",
            )
        }
        P({ classes("bill-muted") }) {
            Text(
                "Pattern: a word, phrase or punctuation mark, in any capitalization. Words match whole words only, * matches the rest of a word (schedul* matches schedule and scheduling), and punctuation matches as typed (; or approx.).",
            )
        }
        BillButton("Suggest checks from my rules", busy || !canSuggest || chatsRemaining <= 0) { suggest() }
        P({ classes("bill-muted") }) {
            Text(
                if (canSuggest) {
                    "BILL reads your saved rules and proposes checks for the ones that name exact wording. Uses one rules chat · $chatsRemaining left today. Nothing is saved until you add the checks you want and save."
                } else {
                    "Save standing rules first; BILL suggests checks from them."
                },
            )
        }
        if (suggestions.isNotEmpty()) {
            Div({
                attr("role", "region")
                attr("aria-label", "Suggested wording checks")
            }) {
                H3 { Text("Suggested by BILL (${suggestions.size})") }
                Div({ classes("bill-row") }) {
                    BillButton("Add all suggestions", busy) {
                        change(suggestions.fold(checks, ::acceptSuggestion))
                        changeSuggestions(emptyList())
                    }
                    BillButton("Dismiss all", busy) { changeSuggestions(emptyList()) }
                }
                suggestions.forEachIndexed { index, suggestion ->
                    Div({ classes("bill-row", "bill-between") }) {
                        Span {
                            Text(wordingCheckSummary(suggestion))
                            if (suggestion.message.isNotBlank()) Text(" — ${suggestion.message}")
                        }
                        Div({ classes("bill-row") }) {
                            BillButton("Add suggestion ${index + 1}", busy) {
                                change(acceptSuggestion(checks, suggestion))
                                changeSuggestions(suggestions.filterIndexed { i, _ -> i != index })
                            }
                            BillButton("Dismiss suggestion ${index + 1}", busy) { changeSuggestions(suggestions.filterIndexed { i, _ -> i != index }) }
                        }
                    }
                }
            }
        }
        H3 { Text(if (checks.isEmpty()) "No checks yet" else "Your checks (${checks.size} of ${WordingCheck.MAX_CHECKS})") }
        checks.forEachIndexed { index, check ->
            fun update(value: WordingCheck) = change(checks.mapIndexed { i, old -> if (i == index) value else old })
            val number = index + 1
            Div({ classes("bill-card") }) {
                Div({ classes("bill-grid") }) {
                    BillField("Check $number pattern", check.pattern, "e.g. regarding") { update(check.copy(pattern = it.take(WordingCheck.MAX_PATTERN))) }
                    BillField("Check $number message", check.message, "e.g. Use \"re\" instead of \"regarding\".") { update(check.copy(message = it.take(WordingCheck.MAX_MESSAGE))) }
                }
                BillCheck("Check $number offers a replacement", check.replacement != null) { update(check.copy(replacement = if (it) "" else null)) }
                check.replacement?.let { replacement ->
                    BillField("Check $number replacement (blank deletes the match)", replacement, "e.g. re") {
                        update(check.copy(replacement = it.take(WordingCheck.MAX_REPLACEMENT)))
                    }
                }
                BillCheck("Check $number blocks approval", check.blocking) { update(check.copy(blocking = it)) }
                BillButton("Remove check $number", busy, "danger") { change(checks.filterIndexed { i, _ -> i != index }) }
            }
        }
        Div({ classes("bill-row") }) {
            BillButton("+ Add check", busy || checks.size >= WordingCheck.MAX_CHECKS) { change(checks + WordingCheck()) }
            BillButton("Save checks", busy || !dirty, "primary") { save() }
            if (dirty) BillButton("Discard check changes", busy) { discard() }
        }
    }
}
